import type { GetProjectChatsRequest, GetProjectChatsResponse, ProjectChatData } from './getProjectChats';
import type { GetRecentProjectChatsRequest, GetRecentProjectChatsResponse } from './getRecentProjectChats';
import type { ProjectData } from './getProjects';
import { findMockProject } from './mockProjects';

type MockChat = Pick<ProjectChatData, 'chatId' | 'title' | 'user' | 'latestActivity' | 'messageCount'> & {
  artefactFileNames: string[];
};

/** UUID-shaped, so opening a mock chat gets the backend's "not found" rather than a validation error. */
export const mockChatId = (projectNumber: number, chatNumber: number) =>
  `00000000-0000-4000-8000-${String(projectNumber).padStart(6, '0')}${String(chatNumber).padStart(6, '0')}`;

/** The chat that generated email_templates_draft.docx in mockProjectFiles.ts, which is also one of its artefacts. */
export const MOCK_CONTENT_STRATEGY_CHAT_ID = mockChatId(1, 2);

const designChat = (
  chatNumber: number,
  title: string,
  user: string,
  latestActivity: string,
  messageCount: number,
  artefactFileNames: string[] = [],
): MockChat => ({ chatId: mockChatId(1, chatNumber), title, user, latestActivity, messageCount, artefactFileNames });

/** The "Q2 Marketing Strategy" chats from the Chat History design; the first three are the landing page tiles. */
const DESIGN_CHATS: MockChat[] = [
  designChat(1, 'Review our competitor analysis and suggest positioning', 'Sam Dale', '2026-05-27T14:30:00Z', 18),
  designChat(2, 'Draft a content strategy based on audience research', 'Jo Smith', '2026-05-25T11:20:00Z', 12, [
    'content_strategy_draft.docx',
    'email_templates_draft.docx',
  ]),
  designChat(3, 'Summarise the budget spreadsheet for the exec team', 'Sam Dale', '2026-05-22T09:45:00Z', 6, [
    'exec_summary.docx',
  ]),
  designChat(4, "What's our social media reach vs competitors?", 'Sam Dale', '2026-05-20T16:05:00Z', 9),
  designChat(5, 'Create a SWOT analysis from all uploaded docs', 'Jo Smith', '2026-05-18T12:15:00Z', 14, [
    'swot_analysis.docx',
  ]),
  designChat(6, 'Draft an email to the exec team about Q2 plans', 'Sam Dale', '2026-05-15T08:40:00Z', 4),
  designChat(7, 'Compare our pricing against competitor_report', 'Alex Jones', '2026-05-14T13:10:00Z', 8),
  designChat(8, 'Identify key audience segments from research data', 'Jo Smith', '2026-05-12T10:25:00Z', 11),
  designChat(9, 'Review brand guidelines for consistency issues', 'Sam Dale', '2026-05-10T15:50:00Z', 7),
  designChat(10, 'Generate talking points for the board meeting', 'Alex Jones', '2026-05-08T09:30:00Z', 5, [
    'board_talking_points.docx',
  ]),
  designChat(11, 'Analyse Q1 results and recommend Q2 adjustments', 'Sam Dale', '2026-05-06T14:00:00Z', 16),
  designChat(12, 'Draft social media copy for product launch', 'Jo Smith', '2026-05-04T11:35:00Z', 3, [
    'social_copy_v1.docx',
  ]),
  designChat(13, 'Summarise partner agreement terms', 'Sam Dale', '2026-05-02T17:20:00Z', 10),
  designChat(14, 'Create a compliance checklist from policies', 'Jo Smith', '2026-04-30T10:45:00Z', 22, [
    'compliance_checklist.docx',
  ]),
  designChat(15, 'Budget variance analysis for finance review', 'Sam Dale', '2026-04-28T13:30:00Z', 8),
  designChat(16, 'Proofread the press release draft', 'Sam Dale', '2026-04-25T09:15:00Z', 2),
  designChat(17, 'Extract action items from meeting notes', 'Alex Jones', '2026-04-22T16:40:00Z', 13),
];

const DESIGN_CHATS_PROJECT_ID = 'mock-project-1';

const GENERATED_TITLES = [
  'Summarise the key points from the uploaded research',
  'List the open actions from the latest status update',
  'Draft a short brief for the next team meeting',
  'Compare the vendor quotes and recommend one',
  'Highlight the main risks in the risk register',
  'Suggest next steps based on the project brief',
] as const;

const GENERATED_ARTEFACT_NAMES = ['research_summary.docx', 'meeting_brief.docx', 'vendor_comparison.xlsx'] as const;

/** Chat counts by project, so some projects have none, some have fewer than three and some need a second page. */
const GENERATED_CHAT_COUNTS = [4, 0, 2, 64, 1] as const;

function buildGeneratedChats(project: ProjectData, projectNumber: number): MockChat[] {
  const count = GENERATED_CHAT_COUNTS[projectNumber % GENERATED_CHAT_COUNTS.length];
  const authors = [project.owner, ...project.sharedWith];
  const newest = new Date(project.lastModifiedDatetime);

  return Array.from({ length: count }, (_, i) => {
    const latestActivity = new Date(newest);
    latestActivity.setUTCHours(latestActivity.getUTCHours() - i * 29);

    return {
      chatId: mockChatId(projectNumber, i + 1),
      title: GENERATED_TITLES[(projectNumber + i) % GENERATED_TITLES.length],
      user: authors[i % authors.length],
      latestActivity: latestActivity.toISOString(),
      messageCount: ((projectNumber + i * 7) % 20) + 1,
      artefactFileNames: i % 3 === 1 ? [GENERATED_ARTEFACT_NAMES[i % GENERATED_ARTEFACT_NAMES.length]] : [],
    };
  });
}

/** Only the listed mock projects have chats; projects created in this session start with none. */
const LISTED_MOCK_PROJECT_ID = /^mock-project-(\d+)$/;

const chatsByProjectId = new Map<string, ProjectChatData[]>();

function getMockProjectChats(projectId: string): ProjectChatData[] | undefined {
  const cached = chatsByProjectId.get(projectId);
  if (cached) return cached;

  const project = findMockProject(projectId);
  if (!project) return undefined;

  const listedNumber = LISTED_MOCK_PROJECT_ID.exec(projectId)?.[1];
  const mockChats =
    projectId === DESIGN_CHATS_PROJECT_ID
      ? DESIGN_CHATS
      : listedNumber
        ? buildGeneratedChats(project, Number(listedNumber))
        : [];

  const chats = mockChats.map(({ artefactFileNames, ...chat }, chatIndex) => ({
    ...chat,
    artefacts: artefactFileNames.map((fileName, artefactIndex) => ({
      fileId: `${projectId}-chat-${chatIndex + 1}-artefact-${artefactIndex + 1}`,
      fileName,
    })),
    lastUpdated: chat.latestActivity,
    createdAt: chat.latestActivity,
    userId: '',
    caseRef: '',
    isDraft: false,
  }));

  chatsByProjectId.set(projectId, chats);
  return chats;
}

const byNewestActivity = (a: ProjectChatData, b: ProjectChatData) =>
  new Date(b.latestActivity).getTime() - new Date(a.latestActivity).getTime();

/** Most recent activity first, the way the real endpoint will. Undefined for an unknown project. */
export function queryMockRecentProjectChats({
  projectId,
  limit,
}: GetRecentProjectChatsRequest): GetRecentProjectChatsResponse | undefined {
  const chats = getMockProjectChats(projectId);
  if (!chats) return undefined;

  const sorted = [...chats].sort(byNewestActivity);

  return { chats: limit === undefined ? sorted : sorted.slice(0, limit) };
}

/** Applies search, the Started by filter, sorting and pagination the way the real endpoint will. Undefined for an unknown project. */
export function queryMockProjectChats({
  projectId,
  pageIndex,
  pageSize,
  sortBy = 'latestActivity',
  sortOrder = 'desc',
  search,
  startedBy,
}: GetProjectChatsRequest): GetProjectChatsResponse | undefined {
  const allChats = getMockProjectChats(projectId);
  if (!allChats) return undefined;

  const searchTerm = search?.trim().toLowerCase();

  const chats = allChats.filter(
    (chat) =>
      (!searchTerm || chat.title.toLowerCase().includes(searchTerm)) &&
      (!startedBy?.length || startedBy.includes(chat.user)),
  );

  // Equal message counts keep the newest chat first.
  const sorted = [...chats].sort((a, b) => {
    const result = sortBy === 'messageCount' ? a.messageCount - b.messageCount : -byNewestActivity(a, b);
    return (sortOrder === 'asc' ? result : -result) || byNewestActivity(a, b);
  });

  const start = pageIndex * pageSize;

  return {
    chats: sorted.slice(start, start + pageSize),
    numberOfResults: sorted.length,
    startedByOptions: [...new Set(allChats.map((chat) => chat.user))].sort((a, b) => a.localeCompare(b)),
  };
}
